import React from 'react';
import PropTypes from 'prop-types';
import Loading from 'zignal-landscape/lib/Loading';
import TimeSeriesChart from 'zignal-landscape/lib/TimeSeriesChart';
import TwitterIcon from 'zignal-landscape/lib/Icon/TwitterIcon';
import FacebookIcon from 'zignal-landscape/lib/Icon/FacebookIcon';
import landscapeColors from 'zignal-landscape/lib/styles/colors.css';
import ChartIcon from 'zignal-landscape/lib/Icon/ChartIcon';
import moment from 'moment-timezone';
import { abbreviateForNewsroom } from 'helpers/numberFormat';
import themeable from 'zignal-landscape/lib/themeable';
import OverviewStory from 'containers/TrendingStoriesOverview/components/OverviewStory';
import InfoBox from 'containers/TrendingStoriesOverview/components/InfoBox';

const CustomTooltip = ({
  x = null,
  y = null
}) => (
  <div style={{textAlign: 'center'}}>
    <small>{moment(x).utc().format('MMM D, YYYY h:mm A')}</small>
    <br />
    <small><b>{y}</b> {y === 1 ? 'Mention' : 'Mentions'}</small>
  </div>
);

CustomTooltip.propTypes = {
  x: PropTypes.any,
  y: PropTypes.any,
};

class StoryDetails extends React.Component {
  renderChart() {
    const { storyDetails, styles } = this.props;
    const chartData = [{
      type: 'line',
      axis: 'y',
      color: landscapeColors.blue07,
      points: storyDetails.timeline,
      label: '',
    }];

    return (
      <div className={styles.chartContainer} data-cy="TrendingStoriesOverviewChart">
        <TimeSeriesChart
          yAxis={{
            title: {
              text: 'Mentions',
              tooltipText: 'Mentions',
            },
          }}
          data={chartData}
          showLegend={false}
          toolTipContentComponent={<CustomTooltip />}
        />
      </div>
    );
  }

  renderHeading(message) {
    const { styles } = this.props;
    return (
      <b className={styles.detailsHeading}>{message}</b>
    );
  }

  renderAnalytics() {
    const { styles, analytics, loadAnalyticsError } = this.props;

    if (loadAnalyticsError) {
      return <InfoBox message="Unable to fetch analytics" inline />;
    }

    return (
      <div className={styles.analyticsContainer}>
        <div className={styles.storyDetailsHeader}>
          {this.renderHeading('Overall Story')}
        </div>
        <div className={styles.analyticsList}>
          <div className={styles.analytic}>
            <span className={styles.mr8}>
              <TwitterIcon height="1.4em" width="1.4em" />
            </span>
            <span className={styles.normalSize}>
              <span className={styles.mr8}>Shares</span>
              <b>{analytics && abbreviateForNewsroom(analytics.twitter_share_count)}</b>
            </span>
          </div>
          <div className={styles.analytic}>
            <span className={styles.mr8}>
              <FacebookIcon height="1.4em" width="1.4em" />
            </span>
            <span className={styles.normalSize}>
              <span className={styles.mr8}>Shares</span>
            </span>
            <b>{analytics && abbreviateForNewsroom(analytics.facebook_share_count)}</b>
          </div>
          <div className={styles.analytic}>
            <span className={styles.mr8}>
              <ChartIcon height="1.4em" width="1.4em" />
            </span>
            <span className={styles.normalSize}>
              <span className={styles.mr8}>Potential Impressions</span>
              <b>{analytics && abbreviateForNewsroom(analytics.impression)}</b>
            </span>
          </div>
        </div>
      </div>
    );
  }

  renderStoryDetails() {
    const { storyDetails: {
      story
    }, styles, timezone, isTwitterRestricted } = this.props;
    return (
      <div className={styles.storyDetailsContainer}>

        { !isTwitterRestricted &&
        <div>
          <div className={styles.storyDetailsHeader}>
            {this.renderHeading('Matches Dataset Terms')}
          </div>
          <div className={styles.storyDetailsSubtitle}>
            Last Shared By:
          </div>
          <div className={styles.storyDetailsStory} data-cy="TrendingStoriesOverviewDetails">
            <OverviewStory story={story} timezone={timezone} />
          </div>
        </div>
        }
        <div className={styles.storyDetailsSubtitle}>
          Complete Story Timeline:
        </div>
        {this.renderChart()}
      </div>
    );
  }

  render() {
    const { storyDetails, loadOverviewError, styles } = this.props;
    const loading = !storyDetails && !loadOverviewError;
    return (
      <React.Fragment>
        {loadOverviewError && <InfoBox
          message="An error happened while trying to load the story overview details, please try again later."
        />}
        { loading && (
          <div className={styles.loadingContainer}>
            <Loading />
          </div>
        )}
        {storyDetails && this.renderStoryDetails()}
        {storyDetails && this.renderAnalytics()}
      </React.Fragment>
    );
  }
}

StoryDetails.propTypes = {
  storyDetails: PropTypes.object,
  styles: PropTypes.object.isRequired,
  loadOverviewError: PropTypes.any,
  timezone: PropTypes.string,
  analytics: PropTypes.object,
  loadAnalyticsError: PropTypes.any,
  isTwitterRestricted: PropTypes.bool,
};

StoryDetails.defaultProps = {
  storyDetails: null,
  loadOverviewError: null,
  timezone: 'EST',
  analytics: null,
  loadAnalyticsError: null
};

export default themeable(StoryDetails);
